/**
 * Data Volume Management Page
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../components/layout/Header';
import { useDataVolumes } from '../hooks/useDataVolumes';
import { useProjects } from '../hooks/useProjects';
import { DataVolume } from '../types/dataVolume';
import { Project } from '../types';
import { useSelectedProject } from '../hooks/useSelectedProject';

const DataVolumes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const pageFromUrl = searchParams.get('page');

  const {
    selectedProjectId: globalProjectId,
    projects,
    setSelectedProject: setGlobalProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Use URL param if available, otherwise use global selection
  const selectedProject = projectFromUrl || globalProjectId;

  const [page, setPage] = useState<number>(pageFromUrl ? parseInt(pageFromUrl, 10) : 1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) {
      autoSelectFirstProject();
    }
  }, [projectFromUrl, autoSelectFirstProject]);

  useEffect(() => {
    if (pageFromUrl) {
      const pageNum = parseInt(pageFromUrl, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setPage(pageNum);
      }
    }
  }, [pageFromUrl]);

  const projectId = selectedProject || undefined;

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedProject) {
      params.set('project', selectedProject);
    }
    if (page > 1) {
      params.set('page', page.toString());
    }
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedProject, page, setSearchParams, searchParams]);

  const {
    volumes,
    totalCount,
    page: currentPage,
    pages,
    isLoading,
    isError,
    refetch,
    createVolume,
    isCreating,
    deleteVolume,
    isDeleting,
  } = useDataVolumes({
    projectId,
    page,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  const handleCreateVolume = () => {
    if (projectId) {
      navigate(`/data-volumes/create?project=${projectId}`);
    } else {
      navigate('/data-volumes/create');
    }
  };

  const handleDeleteVolume = (volumeId: string) => {
    setShowDeleteConfirm(volumeId);
  };

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      deleteVolume(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'creating':
      case 'importing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'deleting':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'deleted':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  return (
    <div>
      <Header
        title="Data Volumes"
        subtitle="Manage standalone disk volumes"
        actions={
          <button
            onClick={handleCreateVolume}
            className="btn-primary flex items-center gap-2"
            disabled={isCreating}
          >
            <PlusIcon className="w-4 h-4" />
            Create Volume
          </button>
        }
      />

      <div className="p-6">
        {/* Stats Card */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">Total Volumes</p>
              <p className="mt-2 text-3xl font-bold text-white">{totalCount}</p>
            </div>
            <div className="bg-primary-500/20 p-3 rounded-xl">
              <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => {
                  const newProject = e.target.value;
                  setGlobalProject(newProject);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-600 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px] transition-colors"
                aria-label="Filter by project"
              >
                <option value="">All Projects</option>
                {projects.map((project: Project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Volumes Table */}
        {isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading data volumes...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <p className="text-red-400 mb-4">Failed to load data volumes</p>
              <button onClick={() => refetch()} className="btn-primary">
                Retry
              </button>
            </div>
          </div>
        ) : volumes.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-800 mb-4">
                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">No Data Volumes</h3>
              <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
                {selectedProject
                  ? "You don't have any data volumes in this project yet. Create your first data volume to get started."
                  : "Select a project above or create a new data volume to get started."}
              </p>
              <div className="mt-8">
                <button
                  onClick={handleCreateVolume}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Data Volume
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Size</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Source Type</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Access Mode</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {volumes.map((volume: DataVolume) => (
                    <tr key={volume.id} className="hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">{volume.name}</div>
                          {volume.description && (
                            <div className="text-sm text-gray-400">{volume.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(volume.status)}`}>
                          {volume.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {volume.size_gb} GB
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white capitalize">
                        {volume.source_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {volume.access_mode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(volume.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDeleteVolume(volume.id)}
                          disabled={isDeleting || volume.status === 'deleting'}
                          className="text-red-400 hover:text-red-300 disabled:opacity-50"
                          title="Delete Volume"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  Showing page {currentPage} of {pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page >= pages}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(null)} />
          <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Delete Data Volume</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete this data volume? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataVolumes;
